import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';

import { placePopover, type PopoverRect } from '../lib/place-popover';

/**
 * What a popover hangs from: an element, whose rect is re-read every frame, or
 * a fixed viewport rect (a pipe's cursor point).
 */
export type PopoverAnchor = HTMLElement | DOMRect;

/**
 * The widget card around the popover (`WidgetCardShell` sets
 * `data-widget-card`), clipped to the viewport. Outside a card — Storybook — the
 * viewport alone.
 */
function boundaryOf(popover: HTMLElement): PopoverRect {
  const viewport = { top: 0, left: 0, right: window.innerWidth, bottom: window.innerHeight };
  const card = popover.closest('[data-widget-card]')?.getBoundingClientRect();
  if (!card) return viewport;
  return {
    top: Math.max(card.top, viewport.top),
    left: Math.max(card.left, viewport.left),
    right: Math.min(card.right, viewport.right),
    bottom: Math.min(card.bottom, viewport.bottom),
  };
}

/**
 * Places a fixed popover with `placePopover` and keeps it on its anchor while
 * mounted: it re-places once per animation frame (the TDEV-960
 * `TopicListPopover` loop), so page scroll, resize and relayout all look the
 * same to one rect read. State only changes when the position does, so idle
 * frames don't re-render.
 */
export function usePopoverPlacement(anchor: PopoverAnchor) {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    const place = () => {
      const popover = ref.current;
      if (!popover) return;
      const next = placePopover({
        anchor: anchor instanceof Element ? anchor.getBoundingClientRect() : anchor,
        popover: { width: popover.offsetWidth, height: popover.offsetHeight },
        boundary: boundaryOf(popover),
      });
      setPosition((prev) => (prev.top === next.top && prev.left === next.left ? prev : next));
    };
    place();
    let frame = requestAnimationFrame(function tick() {
      place();
      frame = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(frame);
  }, [anchor]);

  // The anchored-overlay layer: above the chart columns' `z-10`, below the
  // `z-50` dialog/sheet layer (`ui.md` → Overlay layers).
  const style: CSSProperties = { position: 'fixed', zIndex: 30, ...position };
  return { ref, style };
}
